import * as React from "react";
import {
    GridColumns,
    GridSelectionModel,
    DataGridPremium,
    useGridApiRef,
    GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import {
    Asset,
    AssetVersionStatus,
} from "../../../../../services/types/enterpriseParticipantApiTypes";
import { PersonInfo } from "../../../../../models/personInfo";
import AssessmentRescoredCell from "./assessmentRescoredCell";
import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";
import { addCustomDataGridFilters } from "../../../../../components/common/customDataGridFilters/customDataGridFilters";
import CclDataGridToolbar from "../../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import { UserClaimsService } from "../../../../../services/currentUserService/currentUserService";

type PaxAssetRow = {
    id: number;
    imkey: number;
    firstName: string;
    lastName: string;
    fileName: string;
    type: string;
    isReleased: boolean;
    releaseDate: Date | null;
    createdDate: Date;
    latestVersionId: string;
    latestScoredDate: Date | null;
    versionStatuses: AssetVersionStatus[];
};

export interface ParticipantAssetsDataGridProps {
    assets: Asset[];
    participants: PersonInfo[];
    handleEvent: (eventName: string, selectedIds: (number | string)[]) => void;
    coachReleaseEnabled: boolean;
    clientAdmin?: boolean;
    eventInProgress: string;
    sessionType: number | undefined;
}

export const ParticipantAssetsDataGrid: React.FC<ParticipantAssetsDataGridProps> = (props) => {
    const [paxAssets, setPaxAssets] = React.useState<PaxAssetRow[]>([]);
    const [ids, setIds] = React.useState<(number | string)[]>([]);
    const [groupingColDef, setGroupingColDef] = React.useState<any>({ headerName: "Participant" });
    const apiRef = useGridApiRef();

    function getFullName(params: any) {
        if (params.rowNode.isAutoGenerated) {
            return params.rowNode.groupingKey;
        }
        return `${params.row.firstName || ""} ${params.row.lastName || ""}`;
    }

    const handleRowGroupingModelChange = (model: GridRowGroupingModel) => {
        let groupedHeaders: string[] = [];
        model.forEach((group) => {
            groupedHeaders.push(COLUMNS.find((col) => col.field === group)?.headerName || "Group");
        });
        setGroupingColDef({ headerName: groupedHeaders.join(", ") });
    };

    const COLUMNS = [
        {
            field: "name",
            headerName: "Participant",
            valueGetter: getFullName,
            groupingValueGetter: getFullName,
            type: "string",
            flex: 1,
        },
        {
            field: "fileName",
            headerName: "File",
            type: "string",
            flex: 1,
        },
        {
            field: "type",
            headerName: "File Type",
            type: "string",
            flex: 0.6,
        },
        {
            field: "createdDate",
            headerName: "Created",
            description: "The date and time the original file was stored.",
            valueGetter: (params: any) => new Date(params.row.createdDate),
            type: "date",
            renderCell: (params: any) => {
                if (!params.rowNode.isAutoGenerated) {
                    return <span>{params?.value.toLocaleDateString()}</span>;
                } else {
                    return <span></span>;
                }
            },
            flex: 0.65,
        },
        {
            field: "releaseDate",
            headerName: "Released",
            description:
                "The date and time the LATEST version of the file was released (empty if not released).",
            valueGetter: (params: any) => new Date(params.row.releaseDate),
            groupingValueGetter: (params: any) => new Date(params.row.releaseDate),
            type: "date",
            renderCell: (params: any) => {
                if (params.row.isReleased && params.row.releaseDate != null) {
                    return <span>{params?.value.toLocaleDateString()}</span>;
                }
                return <span></span>;
            },
            flex: 0.65,
        },
        {
            field: "latestScoredDate",
            headerName: "Last Scored",
            description:
                "The date and time the LATEST version of the assessment was scored (empty if not a CCL scored assessment).",
            valueGetter: (params: any) => new Date(params.row.latestScoredDate),
            groupingValueGetter: (params: any) => new Date(params.row.latestScoredDate),
            type: "date",
            renderCell: (params: any) => {
                if (params.row.latestScoredDate != null) {
                    return (
                        <React.Fragment>
                            <span>{params?.value.toLocaleDateString()}</span>
                            {params.row.versionStatuses.length > 1 && (
                                <Tooltip
                                    title={`${params.row.versionStatuses.length} versions`}
                                    enterDelay={150}
                                    leaveDelay={150}
                                >
                                    <InfoIcon color="primary" sx={{ ml: 2 }} />
                                </Tooltip>
                            )}
                        </React.Fragment>
                    );
                }
                return <span></span>;
            },
            flex: 0.65,
        },
        {
            field: "history",
            headerName: "History",
            description: "View dates for all versions of the file.",
            renderCell: (params: any) => {
                if (params.rowNode.type !== "group") {
                    return (
                        <AssessmentRescoredCell
                            latestVersionId={params.row.latestVersionId}
                            versionStatuses={params.row.versionStatuses}
                            isAssessment={params.row.type === "Assessment"}
                        />
                    );
                } else return <div></div>;
            },
            flex: 0.3,
        },
    ];

    React.useEffect(() => {
        if (
            props.assets &&
            props.assets.length > 0 &&
            props.participants &&
            props.participants.length > 0
        ) {
            const tempAssets = props.assets.filter(
                (a) => props.participants.find((p) => p.imKey === a.imKey) != null,
            );
            const pa: PaxAssetRow[] = tempAssets.map((a) => {
                const pax = props.participants.find((p) => p.imKey === a.imKey); // pax is populate because filter above
                const par: PaxAssetRow = {
                    id: a.id,
                    imkey: a.imKey,
                    firstName: pax?.firstName ?? "Unknown",
                    lastName: pax?.lastName ?? "Unknown",
                    fileName: a.name,
                    type: a.fileType,
                    isReleased: a.isReleased,
                    releaseDate: a.releaseDate,
                    createdDate: a.createdDate,
                    versionStatuses: a.versionStatuses,
                    latestVersionId: a.latestVersionId,
                    latestScoredDate: a.latestScoredDate,
                };
                return par;
            });
            setPaxAssets(
                pa.sort((a, b) => {
                    return (
                        a.lastName.localeCompare(b.lastName) ||
                        a.firstName.localeCompare(b.firstName) ||
                        a.fileName.localeCompare(b.fileName)
                    );
                }),
            );
        } else {
            setPaxAssets([]);
        }
    }, [props.assets, props.participants]);

    const claimsService = new UserClaimsService();

    let miscButtons = [
        {
            id: "upload",
            label: "Upload File(s)",
            caption: "Upload File(s)",
            disable: props.participants.length <= 0,
            color: "warning",
            onClick: () => {
                props.handleEvent("upload", ids);
            },
        },
        {
            id: "download",
            label: "Download File(s)",
            caption: "Download File(s)",
            disable: ids?.length <= 0,
            color: "warning",
            onClick: () => {
                props.handleEvent("download", ids);
            },
            loadingBtn: true,
            isLoading: props.eventInProgress === "download",
        },
        {
            id: "release",
            label: "Release File(s)",
            caption: "Release File(s)",
            disable: ids?.length <= 0,
            color: "warning",
            onClick: () => {
                props.handleEvent("release", ids);
            },
            loadingBtn: true,
            isLoading: props.eventInProgress === "release",
        },
        {
            id: "unrelease",
            label: "Un-Release File(s)",
            caption: "Un-Release File(s)",
            disable: ids?.length <= 0,
            color: "warning",
            onClick: () => {
                props.handleEvent("remove", ids);
            },
            loadingBtn: true,
            isLoading: props.eventInProgress === "unrelease",
        },
        {
            id: "delete",
            label: "Delete File(s)",
            caption: "Delete File(s)",
            disable: ids?.length <= 0,
            color: "warning",
            onClick: () => {
                props.handleEvent("delete", ids);
            },
            loadingBtn: true,
            isLoading: props.eventInProgress === "delete",
        },
        {
            id: "coachrelease",
            type: "switch",
            hidden: props.sessionType === 7,
            caption: "Assigned Resources May Release Files",
            checkedState: props.coachReleaseEnabled,
            disable: claimsService.IsReadOnly(),
            onClick: () => {
                props.handleEvent("coachRelease", ids);
            },
        },
    ];

    if (props.clientAdmin) {
        miscButtons = miscButtons.filter((btn) => btn.id === "download");
    }

    return (
        <DataGridPremium
            getRowId={(row) => row.id}
            rows={paxAssets == null ? [] : paxAssets}
            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
            components={{ Toolbar: CclDataGridToolbar }}
            apiRef={apiRef}
            groupingColDef={groupingColDef}
            onRowGroupingModelChange={handleRowGroupingModelChange}
            componentsProps={
                claimsService.IsReadOnly()
                    ? undefined
                    : {
                          toolbar: {
                              showSaveState: true,
                              hideSettingsButtons: true,
                              miscButtons: miscButtons,
                              btnClick: (buttonName: string) => props.handleEvent(buttonName, ids),
                          },
                      }
            }
            disableSelectionOnClick
            checkboxSelection={true}
            onSelectionModelChange={(ids: GridSelectionModel) => {
                setIds(ids.map((i) => i));
            }}
            initialState={{
                sorting: { sortModel: [{ field: "name", sort: "asc" }] },
            }}
            sx={{
                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                },
            }}
        />
    );
};

export default ParticipantAssetsDataGrid;
